import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useState } from 'react';
import { useParams } from "react-router-dom";
import { flattenAndSortBusinessSkills } from "../../utils/flattenAndSortBusinessSkills";
import { fetchRefActivitySectors } from "./fetcher/ref-activity-sectors-fetcher";
import { fetchRefCompetencesCategories } from "./fetcher/ref-competences-categories-fetcher";
import { fetchRefCompetences } from "./fetcher/ref-competences-fetcher";
import { fetchRefContractTypes } from "./fetcher/ref-contract-types-fetcher";
import { fetchRefEducationLevels } from "./fetcher/ref-education-levels-fetcher";
import { fetchRefLanguageLevels } from "./fetcher/ref-language-levels-fetcher";
import { fetchRefLanguages } from "./fetcher/ref-languages-fetcher";
import { fetchResumeData, updateResumeData } from "./fetcher/resume-data-fetcher";
import { businessSkillsActionTypes } from "./reducer/resume-business-skills-reducer";
import { diplomasActionTypes } from "./reducer/resume-diplomas-reducer";
import { experiencesActionTypes } from "./reducer/resume-experiences-reducer";
import { hobbiesActionTypes } from "./reducer/resume-hobbies-reducer"
import { languageSkillsActionTypes } from './reducer/resume-language-skills-reducer';
import { propertiesActionTypes } from "./reducer/resume-properties-reducer";
import { privateActionTypes, resumeActionTypes, resumeReducer } from "./reducer/resume-reducer";

// SAFEGUARD : as actionTypes come from several sources, it's a good idea to check if there are unique
function ensureThereNoDuplicateAction() {
    const allActionTypes = [
        resumeActionTypes,
        privateActionTypes,
        propertiesActionTypes,
        languageSkillsActionTypes,
        businessSkillsActionTypes,
        experiencesActionTypes,
        diplomasActionTypes,
        hobbiesActionTypes,
    ]

    const countActionTypes = allActionTypes
        .reduce( ( previousValue, currentValue ) => previousValue + Object.values( currentValue ).length, 0 );

    const allActionTypeFlat = allActionTypes.reduce( ( previousValue, currentValue ) => {
        const currentActionTypes = Object.values( currentValue );
        for ( const actionType of currentActionTypes ) {
            if ( previousValue.includes( actionType ) ) {
                throw new Error( `Duplicated actionType ${ actionType }, please check your code!` );
            }
        }
        return [ ...previousValue, ...currentActionTypes ];
    }, [] )

    if ( allActionTypeFlat.length !== countActionTypes ) {
        throw new Error( `Inconsistant actionType count: ${ allActionTypeFlat.length } vs ${ countActionTypes }, please check your code!` );
    }
}

export const initialState = {
    properties: {},
    document: {
        user: {},
        introduction: null,
        languageSkills: [],
        businessSkills: [],
        experiences: [],
        diplomas: [],
        hobbies: [],
    }
};

export const ResumeContext = createContext(
    {
        resumeData: initialState,
        refActivitySectors: [],
        refCompetences: [],
        refCompetencesCategories: [],
        refContractTypes: [],
        refEducationLevels: [],
        refLanguageLevels: [],
        refLanguages: [],
        chosenBusinessSkills: [],
        loaded: false,
        validationErrors: {},
        dispatch: () => {
        },
        validateForm: () => {
        },
        handleSave: async () => {
        },
    }
);

export default function ResumeContextProvider( { children } ) {
    const { resumeId } = useParams();
    const [ resumeState, dispatch ] = useReducer( resumeReducer, initialState );
    const [ refLanguages, setRefLanguages ] = useState( [] );
    const [ refLanguageLevels, setRefLanguageLevels ] = useState( [] );
    const [ refCompetencesCategories, setRefCompetencesCategories ] = useState( [] );
    const [ refCompetences, setRefCompetences ] = useState( [] );
    const [ refEducationLevels, setRefEducationLevels ] = useState( [] );
    const [ refActivitySectors, setRefActivitySectors ] = useState( [] );
    const [ refContractTypes, setRefContractTypes ] = useState( [] );
    const [ chosenBusinessSkills, setChosenBusinessSkills ] = useState( [] );

    ensureThereNoDuplicateAction();

    useEffect( () => {
        async function fetchReferentials() {
            try {
                setRefLanguages( await fetchRefLanguages() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Languages: ${ err }` );
            }

            try {
                setRefLanguageLevels( await fetchRefLanguageLevels() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Language Levels: ${ err }` );
            }

            try {
                setRefCompetencesCategories( await fetchRefCompetencesCategories() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Competences Categories: ${ err }` );
            }

            try {
                setRefCompetences( await fetchRefCompetences() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Competences: ${ err }` );
            }

            try {
                setRefEducationLevels( await fetchRefEducationLevels() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Education Levels: ${ err }` );
            }

            try {
                setRefActivitySectors( await fetchRefActivitySectors() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Activity Sectors: ${ err }` );
            }

            try {
                setRefContractTypes( await fetchRefContractTypes() );
            } catch ( err ) {
                throw new Error( `Error fetching Ref Contract Types: ${ err }` );
            }
        }

        async function fetchResume() {
            try {
                const data = await fetchResumeData( resumeId );
                dispatch( {
                    type: privateActionTypes.SET_RESUME_DATA,
                    payload: data,
                } );
            } catch ( err ) {
                throw new Error( `Error fetching Resume data: ${ err }` );
            }
        }

        fetchReferentials().then( () => resumeId && fetchResume() );
    }, [ resumeId ] );

    useEffect( () => {
        if ( resumeState?.resumeData?.document?.businessSkills ) {
            const documentLanguage = resumeState?.resumeData?.properties?.documentLanguage || 'fr';
            setChosenBusinessSkills( flattenAndSortBusinessSkills(
                resumeState.resumeData.document.businessSkills,
                documentLanguage
            ) );
        }
    }, [
        resumeState?.resumeData?.document?.businessSkills,
        resumeState?.resumeData?.properties?.documentLanguage
    ] );

    function validateForm() {
        let validationErrors = {};

        if ( !resumeState.resumeData?.properties?.documentLanguage || resumeState.resumeData.properties.documentLanguage === "" ) {
            validationErrors[ resumeState.resumeData?.properties?.documentLanguage ] = "Sélectionnez la langue de votre cv";
        }

        if ( !resumeState.resumeData?.properties?.keywords || resumeState.resumeData.properties.keywords.length === 0 ) {
            validationErrors.keywords = "Définissez un mot clé pour le CV.";
        }

        if ( !resumeState.resumeData?.document?.introduction || resumeState.resumeData.document.introduction.trim() === "" ) {
            validationErrors.introduction = "Définissez une présentation pour le CV.";
        }

        if ( Object.keys( validationErrors ).length > 0 ) {
            dispatch( {
                type: privateActionTypes.SET_VALIDATION_ERRORS,
                payload: validationErrors,
            } );
        } else {
            handleSave();
        }
        return Object.keys( validationErrors ).length === 0;
    }

    async function handleSave() {
        try {
            const data = await updateResumeData( resumeState.resumeData );
            dispatch( {
                type: privateActionTypes.SET_RESUME_DATA,
                payload: data,
            } );
        } catch ( err ) {
            console.log( `Error fetching Resume data: ${ err }` );
        }
    }

    const contextValue = {
        resumeData: resumeState.resumeData,
        refActivitySectors,
        refCompetences,
        refCompetencesCategories,
        refContractTypes,
        refEducationLevels,
        refLanguageLevels,
        refLanguages,
        chosenBusinessSkills,
        loaded: resumeState.loaded,
        validationErrors: resumeState.validationErrors,
        dispatch: dispatch,
        validateForm: validateForm,
        handleSave: handleSave,
    };

    return <ResumeContext.Provider value={ contextValue }>
        { resumeState.loaded ? children : <div className="text-light p-5">Chargement du CV...</div> }
    </ResumeContext.Provider>;
}

ResumeContextProvider.propTypes = {
    children: PropTypes.node.isRequired,
}
